import { useJobillaOrgId } from '@/hooks'
import {
	BrandingData,
	brandingsIndexQueryKey,
	brandingsShowQueryKey,
	type ImageData,
	mediaImageIndexQueryKey,
	useBrandingGenerate,
	useBrandingsDestroy,
	useBrandingsIndex,
	useBrandingsShow,
	useBrandingsStore,
	useBrandingsUpdate,
	useMediaImageUpload,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { ConformedBranding, logger, MediaAsset, useAlert } from '../ui'
// eslint-disable-next-line import/no-internal-modules -- TODO fix when deprecating old components
import { useBrandLibrary } from '../ui/constructs/branding/simplified'
import { useConformImageDataToMediaAsset } from './helpers'

const POLLING_INTERVAL = 3000

const SCAN_ERROR_KEYS: Record<NonNullable<BrandingData['generationError']>, string> = {
	website_unreachable: 'branding.scanWebsiteUnreachable',
	scan_failed: 'branding.scanIncomplete',
}

/** Extract HTTP error details from Axios errors for logging */
function extractHttpErrorDetails(error: unknown): { status?: number; statusText?: string; responseData?: unknown } {
	if (error && typeof error === 'object' && 'response' in error) {
		const response = (error as { response?: { status?: number; statusText?: string; data?: unknown } }).response
		return {
			status: response?.status,
			statusText: response?.statusText,
			responseData: response?.data,
		}
	}
	return {}
}

export function useSupplyBrandingLibrary({ enableSleeping = true }: { enableSleeping?: boolean } = {}) {
	const [isSleeping, setIsSleeping] = useState(true)
	const [expandedBrandingId, setExpandedBrandingId] = useState<string | null>(null)

	const jobillaOrgId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const { t } = useTranslation('common')
	const alert = useAlert()

	const brandingsIndex = useBrandingsIndex({
		sort: ['created_at'],
		'filter[organisation_id]': jobillaOrgId,
	}, {
		enabled: enableSleeping ? !isSleeping : true,
	})

	const expandedBranding = useBrandingsShow(expandedBrandingId!, {
		enabled: !!expandedBrandingId,
		refetchInterval: ({ state: { data } }) => {
			if (data?.isGenerated !== true) {
				return POLLING_INTERVAL
			}
			return false
		},
	})

	// Invalidate the brand list when generation completes (isGenerated transitions to true)
	const isGenerated = expandedBranding.data?.isGenerated
	const prevIsGenerated = useRef(isGenerated)
	useEffect(() => {
		if (isGenerated === true && prevIsGenerated.current === false) {
			// Generation just completed - invalidate the list so sidebar updates
			queryClient.invalidateQueries({
				queryKey: brandingsIndexQueryKey({
					'sort': ['created_at'],
					'filter[organisation_id]': jobillaOrgId,
				}),
			})
		}
		prevIsGenerated.current = isGenerated
	}, [isGenerated, queryClient, jobillaOrgId])

	const {
		mutateAsync: brandingMutationAsync,
	} = useBrandingsUpdate({
		mutationKey: ['branding-update'],
		onSuccess: () => {
			logger.info('Brand set saved', {
				category: 'branding',
				data: {
					branding_id: expandedBrandingId,
				},
			})
		},
		onSettled: () => {
			// clear master list cache
			queryClient.invalidateQueries({
				queryKey: brandingsIndexQueryKey({
					'sort': ['created_at'],
					'filter[organisation_id]': jobillaOrgId,
				}),
			})
			// clear this specific set cache
			queryClient.invalidateQueries({
				queryKey: brandingsShowQueryKey(expandedBrandingId!),
			})
		},
		onError: (error) => {
			const httpDetails = extractHttpErrorDetails(error)
			logger.error(error, {
				category: 'branding',
				tags: { feature: 'branding_update' },
				data: {
					branding_id: expandedBrandingId,
					...httpDetails,
				},
			})
		},
	})

	// instantiate new branding set
	const {
		mutate: brandingStoreMutation,
	} = useBrandingsStore({
		mutationKey: ['branding-store'],
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: brandingsIndexQueryKey({
					'sort': ['created_at'],
					'filter[organisation_id]': jobillaOrgId,
				}),
			})
			queryClient.invalidateQueries({
				queryKey: brandingsShowQueryKey(expandedBrandingId!),
			})
		},
		onError: (error) => {
			const httpDetails = extractHttpErrorDetails(error)
			logger.error(error, {
				category: 'branding',
				tags: { feature: 'branding_store' },
				data: {
					...httpDetails,
				},
			})
		},
	})

	const scanBranding = useBrandingGenerate({
		mutationKey: ['scan-branding'],
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: brandingsIndexQueryKey({
					'sort': ['created_at'],
					'filter[organisation_id]': jobillaOrgId,
				}),
			})
			queryClient.invalidateQueries({
				queryKey: brandingsShowQueryKey(expandedBrandingId!),
			})
		},
		onError: (error) => {
			const httpDetails = extractHttpErrorDetails(error)
			logger.error(error, {
				category: 'branding',
				tags: { feature: 'branding_scan' },
				data: {
					branding_id: expandedBrandingId,
					...httpDetails,
				},
			})
		},
	})

	const [deletingBrandingId, setDeletingBrandingId] = useState<string | null>(null)

	const { mutate: brandingDestroyMutation } = useBrandingsDestroy({
		mutationKey: ['branding-destroy'],
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: brandingsIndexQueryKey({
					'sort': ['created_at'],
					'filter[organisation_id]': jobillaOrgId,
				}),
			})
		},
		onError: (error) => {
			const httpDetails = extractHttpErrorDetails(error)
			logger.error(error, {
				category: 'branding',
				tags: { feature: 'branding_destroy' },
				data: {
					...httpDetails,
				},
			})

			alert.open(
				t('branding.deleteFailedTitle'),
				t('branding.deleteFailedMessage'),
				t('common.ok'),
				() => {},
			)
		},
		onSettled: () => {
			setDeletingBrandingId(null)
		},
	})

	const handleDelete = useCallback((brandingId: string, onSuccess: () => void) => {
		setDeletingBrandingId(brandingId)
		brandingDestroyMutation({ branding: brandingId }, { onSuccess })
	}, [brandingDestroyMutation])

	const handleExpandedBrandingChange = useCallback(async (b: ConformedBranding) => {
		await brandingMutationAsync({
			branding: expandedBrandingId!,
			data: {
				id: expandedBrandingId,
				name: b.name,
				logoId: b.logoId,
				background: b.background,
				foreground: b.foreground,
				accentBackground: b.accentBackground,
				accentForeground: b.accentForeground,
				toneOfVoice: b.toneOfVoice,
				visualGuidance: b.visualGuidance,
				website: b.website,
				fonts: {
					body: b.bodyFont,
					title: b.titleFont,
				},
				generateKeyImage: b.generateKeyImage,
			},
		})
	}, [expandedBrandingId, brandingMutationAsync])

	const handleScanUrl = useCallback((
		url: string,
		onSuccess: () => void,
		onError: (errorKey: string) => void,
	) => {
		// URL should already be normalized by the UI component
		// This validation is a safety check only
		const urlSchema = z.string().url('Invalid URL format')
		const validation = urlSchema.safeParse(url)

		if (!validation.success) {
			const errorMessage = validation.error.issues[0]?.message || 'Invalid URL format'

			logger.warn('Brand scan URL validation failed (safety check)', {
				category: 'branding',
				data: {
					url,
					error: errorMessage,
					branding_id: expandedBrandingId,
				},
			})

			onError('branding.urlInvalid')
			return
		}

		scanBranding.mutate({
			data: {
				url,
				id: expandedBrandingId!,
			},
		}, {
			onSuccess,
			onError: (error) => {
				if (extractHttpErrorDetails(error).status === 422) {
					onError('branding.urlInvalid')
					return
				}
				onError('branding.scanFailed')
			},
		})
	}, [expandedBrandingId, scanBranding])

	// asset upload for logo
	const { mutateAsync: logoUploadMutationAsync } = useMediaImageUpload({
		mutationKey: ['media-image-upload'],
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: mediaImageIndexQueryKey(),
			})
			logger.info('Media image upload successful', {
				category: 'branding',
				data: {
					branding_id: expandedBrandingId,
				},
			})
		},
		onError: (error) => {
			logger.error(error, {
				category: 'branding',
				tags: { feature: 'media_image_upload' },
			})
		},
	})

	const conformImageDataToMediaAsset = useConformImageDataToMediaAsset()

	const handleLogoUpload = useCallback((logo: File, onSuccess: (media: MediaAsset) => void, onFail?: () => void) => {
		const uploadBrandingId = expandedBrandingId

		logger.track('brand_logo_upload_started', {
			category: 'branding',
			data: {
				file_size: logo.size,
				file_type: logo.type,
				branding_id: uploadBrandingId,
			},
		})

		logoUploadMutationAsync({
			organisation: jobillaOrgId,
			data: {
				media: logo,
				collection: 'logo',
			},
		}).then((data) => {
			const asset = data as ImageData

			logger.track('brand_logo_upload_completed', {
				category: 'branding',
				data: {
					file_size: logo.size,
					file_type: logo.type,
					branding_id: uploadBrandingId,
					asset_id: asset.id,
				},
			})

			onSuccess(conformImageDataToMediaAsset(asset))
		}).catch((error) => {
			const httpDetails = extractHttpErrorDetails(error)
			logger.error(error, {
				category: 'branding',
				tags: { feature: 'brand_logo_upload' },
				data: {
					file_size: logo.size,
					file_type: logo.type,
					branding_id: uploadBrandingId,
					...httpDetails,
				},
			})

			onFail?.()

			alert.open(
				'Upload failed',
				'Failed to upload logo. Please try again.',
				'OK',
				() => {},
			)
		})
	}, [
		logoUploadMutationAsync,
		conformImageDataToMediaAsset,
		alert,
		expandedBrandingId,
		jobillaOrgId,
	])

	// -------------
	const { store } = useBrandLibrary({
		isPending: brandingsIndex.isPending,
		onWakeUp: () => {
			setIsSleeping(false)
		},
		onSleep: () => {
			setIsSleeping(true)
		},
		brandingSets: brandingsIndex.data?.map((branding: BrandingData) => ({
			id: branding.id ?? '',
			name: branding.name ?? null,
			logoId: branding.logoId ?? '',
			logo: branding.logo ? conformImageDataToMediaAsset(branding.logo) : undefined,
			foreground: branding.foreground,
			background: branding.background,
			accentForeground: branding.accentForeground ?? branding.foreground,
			accentBackground: branding.accentBackground ?? branding.background,
			toneOfVoice: branding.toneOfVoice ?? '',
			visualGuidance: branding.visualGuidance ?? '',
			website: branding.website ?? '',
			bodyFont: branding.fonts?.body ?? '',
			titleFont: branding.fonts?.title ?? '',
			generateKeyImage: branding.generateKeyImage,
		})),
		onSetExpandedBrandingId: setExpandedBrandingId,
		expandedBrandingSet: expandedBranding.data
			? {
				id: expandedBranding.data.id ?? '',
				name: expandedBranding.data.name ?? null,
				logoId: expandedBranding.data.logoId ?? '',
				logo: expandedBranding.data.logo
					? conformImageDataToMediaAsset(expandedBranding.data.logo)
					: undefined,
				foreground: expandedBranding.data.foreground,
				background: expandedBranding.data.background,
				accentForeground: expandedBranding.data.accentForeground ?? expandedBranding.data.foreground,
				accentBackground: expandedBranding.data.accentBackground ?? expandedBranding.data.background,
				toneOfVoice: expandedBranding.data.toneOfVoice ?? '',
				visualGuidance: expandedBranding.data.visualGuidance ?? '',
				website: expandedBranding.data.website ?? '',

				bodyFont: expandedBranding.data.fonts?.body ?? '',
				titleFont: expandedBranding.data.fonts?.title ?? '',
				generateKeyImage: expandedBranding.data.generateKeyImage,
			}
			: undefined,

		expandedBrandingVersion: expandedBranding.data?.updatedAt ?? null,

		isExpandedBrandingGenerating: !expandedBranding.data?.isGenerated,
		isExpandedBrandingPending: expandedBranding.isPending,
		isExpandedBrandingReady: expandedBranding.isSuccess && !!expandedBranding.data,
		expandedBrandingScanError: expandedBranding.data?.generationError
			? SCAN_ERROR_KEYS[expandedBranding.data.generationError]
			: null,
		onExpandedBrandingChange: handleExpandedBrandingChange,

		onScanUrl: handleScanUrl,
		onNew: (onSuccess: (branding: string) => void) => {
			brandingStoreMutation({
				data: {},
			}, {
				onSuccess: (data) => {
					onSuccess(data.id ?? '')
				},
			})
		},
		onLogoUpload: handleLogoUpload,
		onDelete: handleDelete,
		deletingBrandingId,
	})

	return { store }
}
